// @src/layout/header/mobile/MobileMenu.tsx
'use client'

import React, { useRef, useEffect, useMemo } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'

import { useProfileMode } from '@/hooks/useProfileMode'
import { getMobileHeaderMenu, getMobilePublicClientMenu, getMobilePublicFreelancerMenu, computeGroupActive } from '../utils/headerMenuConfig'
import type { HeaderMenuGroup, HeaderMenuItem } from '../utils/headerMenuConfig'
import { getPathContext, getScopeByPathname } from '../utils/getScopeByPathname'

type RouterLike = {
  push: (href: string) => void
  replace?: (href: string) => void
  refresh?: () => void
}

interface MobileMenuProps {
  showMobileMenu: boolean
  setShowMobileMenu: (show: boolean) => void
  user: any
  unreadCount: number
  router: RouterLike
}

const FREELANCER_ONBOARDING_PATH = '/onboarding/freelancer/profile'
const COMPANY_ONBOARDING_PATH = '/onboarding/enterprise/company'

const FREELANCER_HOME = '/'
const CLIENT_HOME = '/client'

const CLIENT_PROJECT_KEYS = new Set(['project-register', 'estimate-request', 'quotes'])
const CLIENT_COMMUNICATION_KEYS = new Set(['chat'])
const CLIENT_SUPPORT_KEYS = new Set(['inquiry'])

export default function MobileMenu({
  showMobileMenu,
  setShowMobileMenu,
  user,
  unreadCount,
  router,
}: MobileMenuProps) {
  const pathname = usePathname() || ''
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousFocusedElementRef = useRef<HTMLElement | null>(null)

  const {
    loading: modeLoading,
    hasCompany,
    hasFreelancer,
    isCompany: isCompanyMode,
    isFreelancer: isFreelancerMode,
  } = useProfileMode()
  const scope = useMemo(() => {
    return getScopeByPathname(pathname, {
      isLoggedIn: !!user,
      hasActiveFreelancer: isFreelancerMode && !modeLoading,
      hasActiveCompany: isCompanyMode && !modeLoading,
      hasFreelancer,
      hasCompany,
    })
  }, [pathname, user, hasFreelancer, hasCompany, modeLoading, isFreelancerMode, isCompanyMode])

  const pathContext = useMemo(() => {
    return getPathContext(pathname, {
      isLoggedIn: !!user,
      hasActiveFreelancer: isFreelancerMode && !modeLoading,
      hasActiveCompany: isCompanyMode && !modeLoading,
      hasFreelancer,
      hasCompany,
    })
  }, [pathname, user, hasFreelancer, hasCompany, modeLoading, isFreelancerMode, isCompanyMode])

  const forcedScope = useMemo(() => {
    if (pathname.startsWith('/my/client') || pathname.startsWith('/client')) return 'client' as const
    if (pathname.startsWith('/my/freelancer') || pathname.startsWith('/freelancer') || pathname === '/') return 'freelancer' as const
    return scope
  }, [pathname, scope])

  const isClient = forcedScope === 'client'
  const isFreelancer = forcedScope === 'freelancer' || forcedScope === 'public'

  useEffect(() => {
    if (!showMobileMenu) return

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    previousFocusedElementRef.current = document.activeElement as HTMLElement | null
    closeButtonRef.current?.focus()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMobileMenu(false)

      if (e.key === 'Tab' && mobileMenuRef.current) {
        const focusable = mobileMenuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        const active = document.activeElement as HTMLElement | null

        if (e.shiftKey) {
          if (!active || active === first) {
            e.preventDefault()
            last.focus()
          }
        } else if (!active || active === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', onKeyDown)
      previousFocusedElementRef.current?.focus?.()
    }
  }, [showMobileMenu, setShowMobileMenu])

  useEffect(() => {
    if (!showMobileMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMobileMenu, setShowMobileMenu])

  useEffect(() => {
    if (!showMobileMenu) return
    setShowMobileMenu(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const go = (href: string) => {
    router.push(href)
    setShowMobileMenu(false)
  }

  const menuGroups = useMemo<HeaderMenuGroup[]>(() => {
    if (!showMobileMenu) return []

    if (pathContext.isMyPage) {
      if (pathname.startsWith('/my/client')) {
        return computeGroupActive([
          {
            section: 'drawer',
            title: '관리',
            items: [
              { key: 'projects', label: '프로젝트 관리', href: '/my/client/projects', activeMatchers: ['/my/client/projects'] },
              { key: 'team-members', label: '팀 멤버 관리', href: '/my/client/settings/team-members', activeMatchers: ['/my/client/settings/team-members'] },
              { key: 'billing', label: '결제 관리', href: '/my/client/billing', activeMatchers: ['/my/client/billing', '/my/billing'] },
              { key: 'interests', label: '관심', href: '/my/client/interests', activeMatchers: ['/my/client/interests'] },
              { key: 'settings', label: '설정', href: '/my/client/settings', activeMatchers: ['/my/client/settings'] },
            ],
          },
        ], pathname)
      }

      if (pathname.startsWith('/my/freelancer')) {
        return computeGroupActive([
          {
            section: 'drawer',
            title: '관리',
            items: [
              { key: 'profile', label: '프로필 관리', href: '/my/freelancer/profile?tab=basic', activeMatchers: ['/my/freelancer/profile'] },
              { key: 'team', label: '팀 관리', href: '/my/freelancer/teams', activeMatchers: ['/my/freelancer/teams', '/my/freelancer/team'] },
              { key: 'estimates', label: '견적 관리', href: '/my/freelancer/estimates', activeMatchers: ['/my/freelancer/estimates'] },
              { key: 'settings', label: '설정', href: '/my/freelancer/settings', activeMatchers: ['/my/freelancer/settings'] },
            ],
          },
        ], pathname)
      }

      const allGroups = getMobileHeaderMenu(
        null,
        hasCompany,
        hasFreelancer,
        pathname
      )
      return allGroups.filter((group) => group.title === '관리')
    }

    const isLoggedIn = Boolean(user)

    if (isClient) {
      return getMobilePublicClientMenu(isLoggedIn, pathname)
    }

    if (isFreelancer) {
      return getMobilePublicFreelancerMenu(isLoggedIn, pathname)
    }

    return []
  }, [showMobileMenu, pathname, pathContext.isMyPage, isClient, isFreelancer, user, hasCompany, hasFreelancer])

  const normalizedMenuGroups = useMemo<HeaderMenuGroup[]>(() => {
    if (!isClient) return menuGroups

    const allItems = menuGroups.flatMap((group) => group.items)
    const projects = allItems.filter((item) => CLIENT_PROJECT_KEYS.has(item.key))
    const communication = allItems.filter((item) => CLIENT_COMMUNICATION_KEYS.has(item.key))
    const support = allItems
      .filter((item) => CLIENT_SUPPORT_KEYS.has(item.key))
      .map((item) => ({ ...item, label: '고객센터' }))
    const account = allItems.filter(
      (item) =>
        !CLIENT_PROJECT_KEYS.has(item.key) &&
        !CLIENT_COMMUNICATION_KEYS.has(item.key) &&
        !CLIENT_SUPPORT_KEYS.has(item.key)
    )

    const regrouped: HeaderMenuGroup[] = []
    if (projects.length > 0) regrouped.push({ section: 'drawer', title: '프로젝트', items: projects })
    if (communication.length > 0) regrouped.push({ section: 'drawer', title: '커뮤니케이션', items: communication })
    if (support.length > 0) regrouped.push({ section: 'drawer', title: '지원', items: support })
    if (account.length > 0) regrouped.push({ section: 'drawer', title: '계정', items: account })

    return regrouped
  }, [isClient, menuGroups])

  const goFreelancer = () => go(hasFreelancer ? FREELANCER_HOME : FREELANCER_ONBOARDING_PATH)
  const goClient = () => go(hasCompany ? CLIENT_HOME : COMPANY_ONBOARDING_PATH)

  const showFreelancerButton = isClient || (!isClient && !isFreelancer)
  const showClientButton = isFreelancer || (!isClient && !isFreelancer)
  const profileManageHref = isClient ? '/my/client/settings' : '/my/freelancer/profile?tab=basic'
  const userDisplayName = (user?.email?.split('@')[0] || '사용자').slice(0, 20)
  const userAvatarLabel = userDisplayName[0]?.toUpperCase() || 'U'

  if (!showMobileMenu) return null

  return (
    <>
      <button
        type="button"
        aria-label="메뉴 닫기"
        className="fixed inset-0 z-[900] bg-black/40 md:hidden"
        onClick={() => setShowMobileMenu(false)}
      />

      <div
        ref={mobileMenuRef}
        id="mobile-header-menu-drawer"
        role="dialog"
        aria-modal="true"
        className="fixed right-0 top-0 z-[901] h-[100svh] w-[86vw] max-w-[360px] bg-white shadow-xl md:hidden"
      >
        <div className="sticky top-0 z-10 border-b border-gray-100 bg-white/90 backdrop-blur px-4 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">
              {pathContext.isMyPage ? '관리 메뉴' : '메뉴'}
            </p>
            <button
              type="button"
              aria-label="닫기"
              ref={closeButtonRef}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl transition hover:bg-gray-100"
              onClick={() => setShowMobileMenu(false)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="h-[calc(100svh-52px)] overflow-y-auto bg-gray-50 p-4 space-y-4">
          {user && (
            <section className="rounded-2xl bg-white p-4 space-y-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500">프로필</p>

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                  {userAvatarLabel}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{userDisplayName}</p>
                  <p className="text-xs text-gray-500">현재 모드: {isClient ? '고객' : '프리랜서'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => go(isClient ? '/my/client/settings' : '/my/freelancer/settings')}
                  className="ml-auto rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  설정
                </button>
              </div>

              <button
                type="button"
                onClick={() => go(profileManageHref)}
                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {isClient ? '내 프로필 관리' : '메이커 프로필 관리'}
              </button>

              {modeLoading ? (
                <p className="text-sm text-gray-600">프로필 정보를 불러오는 중입니다...</p>
              ) : (
                <div className="rounded-xl bg-gray-100 p-1">
                  {showFreelancerButton && (
                    <button
                      type="button"
                      onClick={goFreelancer}
                      className={[
                        'w-full min-h-[44px] rounded-lg px-3 py-2 text-left text-sm font-semibold whitespace-nowrap transition',
                        isFreelancer ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-700 hover:bg-white/70',
                      ].join(' ')}
                    >
                      프리랜서 모드로 전환
                    </button>
                  )}

                  {showClientButton && (
                    <button
                      type="button"
                      onClick={goClient}
                      className={[
                        'w-full min-h-[44px] rounded-lg px-3 py-2 text-left text-sm font-semibold whitespace-nowrap transition',
                        isClient ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-700 hover:bg-white/70',
                      ].join(' ')}
                    >
                      고객으로 전환
                    </button>
                  )}
                </div>
              )}
            </section>
          )}

          <div className="space-y-4">
            {normalizedMenuGroups.map((group, groupIndex) => (
              <section
                key={`${group.section}-${group.title ?? groupIndex}`}
                className="rounded-2xl bg-white p-1 shadow-sm"
              >
                {group.title && (
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-500">{group.title}</h3>
                  </div>
                )}

                <div className="space-y-0.5">
                  {group.items.map((item: HeaderMenuItem) => {
                    const href = item.href
                    if (typeof href !== 'string' || href.length === 0) return null

                    const isActive = Boolean(item.isActive)

                    const badge =
                      (item.key === 'inbox' || item.key === 'chat') && unreadCount > 0 ? (
                        <span className="inline-flex min-w-[22px] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                          {unreadCount}
                        </span>
                      ) : null

                    if (item.isPrimary) {
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => go(href)}
                          className="w-full min-h-[44px] rounded-xl bg-blue-600 px-4 py-3 text-left text-sm font-semibold text-white whitespace-nowrap transition hover:bg-blue-700 active:scale-[0.99]"
                        >
                          <div className="flex items-center gap-3">
                            <span className="whitespace-nowrap">{item.label}</span>
                            <span className="ml-auto text-white/80">→</span>
                          </div>
                        </button>
                      )
                    }

                    return (
                      <Link
                        key={item.key}
                        href={href}
                        onClick={() => setShowMobileMenu(false)}
                        className={[
                          'flex min-h-[44px] items-center gap-2 rounded-xl px-3 py-3 text-sm transition',
                          isActive
                            ? 'bg-blue-50/70 text-blue-700 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50',
                        ].join(' ')}
                      >
                        <span
                          className={[
                            'h-5 w-0.5 rounded-full transition',
                            isActive ? 'bg-blue-600 opacity-100' : 'bg-transparent opacity-0',
                          ].join(' ')}
                          aria-hidden="true"
                        />
                        <span className="whitespace-nowrap">{item.label}</span>
                        {badge ? <span className="ml-1">{badge}</span> : null}
                      </Link>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>

          {!user && (
            <p className="text-xs text-gray-500 leading-relaxed">일부 기능은 로그인 후 이용할 수 있어요.</p>
          )}
        </div>
      </div>
    </>
  )
}
